import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useId, useState, type ReactNode } from "react";
import { z } from "zod";

import type { RevolutAccounts } from "@ravnar/api-contract";

import { exchangeRatesQueryOptions } from "../../api/exchange-rates";
import { revolutAccountsQueryOptions } from "../../api/revolut";
import { useSession } from "../../api/session";
import { companyIdentityQueryOptions } from "../../api/settings";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { Card, cardActionClassName, CardHeader } from "../../components/ui/card";
import { Empty } from "../../components/ui/empty";
import { Metric } from "../../components/ui/metric";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, type TableColumn } from "../../components/ui/table";
import { TabPanel, Tabs } from "../../components/ui/tabs";
import { MONTHLY_BURN } from "../../demo-data/finance";
import { RATES_STALE_AFTER_DAYS, type CurrencyAmount } from "../../domain/exchange-rates";
import {
	cashPosition,
	revolutCashRows,
	type CashPosition,
	type CashRow,
	type UnconvertedReason,
} from "../../domain/revolut";
import { cn } from "../../lib/cn";
import { formatMoney, formatMonthDay, formatRate, formatShortDate, formatUsd } from "../../lib/format";
import { PayablesCard, ReceivablesCard } from "./-components/payments-cards";
import { ReconciliationCard } from "./-components/reconciliation-card";

const FINANCE_TABS = [
	{ value: "overview", label: "Overview" },
	{ value: "plan", label: "Plan" },
	{ value: "books", label: "Books" },
] as const;

const financeSearchSchema = z.object({
	tab: z.enum(["overview", "plan", "books"]).default("overview").catch("overview"),
});

export const Route = createFileRoute("/finance/")({
	validateSearch: financeSearchSchema,
	search: { middlewares: [stripSearchParams({ tab: "overview" })] },
	head: () => ({ meta: [{ title: "Finance · Ravnar" }] }),
	staticData: { crumbs: ["Finance"] },
	component: FinancePage,
});

/**
 * What an amount in the cash table comes to in the primary currency, on a line under it, when the cash is converted:
 * "≈ USD 37,939", or that the ECB quotes no rate for its currency. Nothing for the primary currency itself.
 */
function convertedLine(position: CashPosition, amount: number, currency: string): ReactNode {
	if (position.kind !== "converted" || currency === position.conversion.target) {
		return null;
	}
	const { target, converted } = position.conversion;
	const found = converted.find(each => each.currency === currency);
	return found === undefined ? (
		<span className="block text-tiny whitespace-nowrap text-warn">No ECB rate</span>
	) : (
		<span className="block text-tiny whitespace-nowrap text-ink-3">
			≈ {formatMoney(amount * found.rate, target)}
		</span>
	);
}

/** The cash table's columns: each account, or a currency's total, with its balance and what that comes to converted. */
function cashColumns(position: CashPosition): readonly TableColumn<CashRow>[] {
	return [
		{
			key: "account",
			header: "Account",
			cell: row =>
				row.kind === "account" ? (
					`Revolut ${row.account.currency} · ${row.account.name}`
				) : (
					<span className="font-semibold">{`${row.currency} total · ${plural(row.accounts, "account", "accounts")}`}</span>
				),
		},
		{
			key: "balance",
			header: "Balance",
			align: "right",
			cell: row => {
				// An amount never wraps between its code and its figure, however narrow the card.
				if (row.kind === "total") {
					return (
						<>
							<span className="font-semibold whitespace-nowrap">
								{formatMoney(row.amount, row.currency)}
							</span>
							{convertedLine(position, row.amount, row.currency)}
						</>
					);
				}
				const { balance } = row.account;
				if (balance === undefined) {
					return "—";
				}
				const amount = Number(balance.amount);
				return (
					<>
						<span className="whitespace-nowrap">{formatMoney(amount, balance.currency)}</span>
						{convertedLine(position, amount, balance.currency)}
					</>
				);
			},
		},
	];
}

function plural(count: number, singular: string, pluralForm: string): string {
	return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * The organization's cash, as the Revolut connection reads it: the Revolut accounts with their balances, and what they
 * come to in the primary currency (Settings › Identity) at the ECB's reference rates. Only a member who may read
 * Finance is sent the accounts or asks for the rates, so for anyone else nothing is asked and the answer is undefined.
 * The sidebar has usually read the primary currency already; without it, or without rates, the cash is not converted.
 */
function useCash() {
	const { member } = useSession();
	const canRead = member.access.finance !== "none";
	const identity = useQuery(companyIdentityQueryOptions());
	const rates = useQuery({ ...exchangeRatesQueryOptions(), enabled: canRead });
	// How old the rates are is counted from when the page opened.
	const [now] = useState(() => Date.now());
	return {
		canRead,
		canConnect: member.role !== "member",
		primary: identity.data?.currency,
		rates: rates.data,
		now,
		// Rates that fail to arrive leave the cash unconverted, but it waits for them rather than change once shown.
		detailsPending: identity.isPending || rates.isPending,
		...useQuery({ ...revolutAccountsQueryOptions(), enabled: canRead }),
	};
}

/**
 * Each currency's total as a list whose items never break inside, so a code always stays with its amount:
 * "USD 14,700  AUD 5,300  EUR 3,280  GBP 28,628".
 */
function CashByCurrency({ totals }: { readonly totals: readonly CurrencyAmount[] }) {
	return (
		<ul aria-label="Cash by currency" className="mb-1 flex flex-wrap gap-x-3 text-caption text-ink-2 tabular-nums">
			{totals.map(total => (
				<li key={total.currency} className={cn("whitespace-nowrap", total.amount < 0 && "text-alert")}>
					{formatMoney(total.amount, total.currency)}
				</li>
			))}
		</ul>
	);
}

function FinancePage() {
	const { tab } = Route.useSearch();
	const navigate = Route.useNavigate();
	const tabsId = useId();

	return (
		<>
			<PageHeader
				title="Finance"
				description="Your position, and the short list of things only you can decide."
				actions={
					<>
						<Button>Export</Button>
						<Button>Refresh</Button>
						<Button variant="primary">Setup & admin</Button>
					</>
				}
			/>
			<div className="mb-5">
				<Tabs
					id={tabsId}
					label="Finance views"
					items={FINANCE_TABS}
					value={tab}
					onChange={next => {
						void navigate({ search: { tab: next }, replace: true });
					}}
				/>
			</div>
			<TabPanel id={tabsId} value={tab}>
				{tab === "overview" ? (
					<FinanceOverview />
				) : (
					<Card>
						<Empty>This view is not built yet.</Empty>
					</Card>
				)}
			</TabPanel>
		</>
	);
}

function FinanceOverview() {
	return (
		<>
			<div className="mb-[22px] grid grid-cols-1 gap-[11px] md:grid-cols-2">
				<CashOnHandMetric />
				<Metric
					label="Recurring burn"
					value={`${formatUsd(MONTHLY_BURN.payroll + MONTHLY_BURN.tools)} /mo`}
					sub={`Payroll ${formatUsd(MONTHLY_BURN.payroll)} · tools ${formatUsd(MONTHLY_BURN.tools)}`}
					tone="warn"
				/>
			</div>
			<div className="mb-3.5 grid grid-cols-1 gap-3.5 lg:grid-cols-3">
				<CashCard />
				<ReceivablesCard />
				<PayablesCard />
			</div>
			<ReconciliationCard />
		</>
	);
}

/** Why there is no cash to show, in a few words, for the tile. */
const NO_CASH: Readonly<Record<Exclude<RevolutAccounts["status"], "connected">, string>> = {
	unavailable: "No bank connected",
	"not-connected": "No bank connected",
	reconnect: "Revolut needs reconnecting",
};

/** Why several currencies are shown unconverted, after the provenance line. */
const NOT_CONVERTED: Readonly<Record<UnconvertedReason, string>> = {
	"no-rates": "Not converted: no exchange rates",
	"no-primary-currency": "Not converted: no primary currency",
	"no-rate-for-any": "Not converted: no ECB rate for these currencies",
};

/**
 * Parts of a line joined by " · ", each kept whole with no-break spaces, so a narrow tile wraps the line only between
 * them: never "ECB rates of Sep" over "25".
 */
function partsOfLine(...parts: readonly string[]): string {
	return parts.map(part => part.replaceAll(" ", "\u{A0}")).join(" · ");
}

/** Whether rates of `ageInDays` are old enough to say so. */
function isStale(ageInDays: number): boolean {
	return ageInDays >= RATES_STALE_AFTER_DAYS;
}

/** The totals the ECB quotes no rate for, left out of a converted total, each kept whole: "AED 12,000 · SAR 5,000". */
function leftOut(amounts: readonly CurrencyAmount[]): string {
	return amounts.map(each => formatMoney(each.amount, each.currency).replaceAll(" ", "\u{A0}")).join(" · ");
}

/**
 * The organization's cash from the Revolut connection. One currency's total is the figure, exact. Several are converted
 * into the primary currency at the ECB's reference rates — "≈ USD 60,105", each currency's own total listed under it,
 * the day of the rates, and any currency the ECB does not quote named and left out — or, when they cannot be converted,
 * counted over each one's total, which adds nothing unlike.
 */
function CashOnHandMetric() {
	const { canRead, data, isPending, isError, primary, rates, now, detailsPending } = useCash();
	if (!canRead) {
		return <Metric label="Cash on hand" value="—" sub="Only Finance can see it" />;
	}
	if (isPending || detailsPending) {
		return <Metric label="Cash on hand" value="—" sub="Reading Revolut…" />;
	}
	if (isError) {
		return <Metric label="Cash on hand" value="—" sub="Revolut could not be read" />;
	}
	if (data.status !== "connected") {
		return <Metric label="Cash on hand" value="—" sub={NO_CASH[data.status]} />;
	}
	const position = cashPosition(data.accounts, rates, primary, now);
	const accounts = plural(data.accounts.length, "account", "accounts");
	const from = partsOfLine("From Revolut", accounts);
	if (position.kind === "none" || position.kind === "single") {
		return (
			<Metric
				label="Cash on hand"
				value={position.kind === "none" ? "—" : formatMoney(position.total.amount, position.total.currency)}
				sub={from}
			/>
		);
	}
	if (position.kind === "unconverted") {
		return (
			<Metric
				label="Cash on hand"
				value={`${String(position.totals.length)} currencies`}
				sub={
					<>
						<CashByCurrency totals={position.totals} />
						{partsOfLine("From Revolut", accounts, NOT_CONVERTED[position.reason])}
					</>
				}
			/>
		);
	}
	const { conversion, approximate, date, ageInDays } = position;
	return (
		<Metric
			label="Cash on hand"
			value={`${approximate ? "≈ " : ""}${formatMoney(conversion.total, conversion.target)}`}
			tone={conversion.total < 0 ? "alert" : undefined}
			sub={
				<>
					<CashByCurrency totals={[...conversion.converted, ...conversion.unconverted]} />
					{approximate ? (
						<span className={cn(isStale(ageInDays) && "text-warn")}>
							{isStale(ageInDays)
								? partsOfLine(
										"From Revolut",
										accounts,
										`ECB rates of ${formatMonthDay(date)}`,
										`${String(ageInDays)} days old`,
									)
								: partsOfLine("From Revolut", accounts, `ECB rates of ${formatMonthDay(date)}`)}
						</span>
					) : (
						// Only the primary currency converted, at 1: no rate was used, so none is credited.
						from
					)}
					{conversion.unconverted.length === 0 ? null : (
						<span className="block text-warn">{`Not in the total: ${leftOut(conversion.unconverted)} (no ECB rate)`}</span>
					)}
				</>
			}
		/>
	);
}

/** The Revolut accounts with their balances, under a header that takes an owner or admin to the Revolut settings. */
function CashCard() {
	const { canConnect } = useCash();
	return (
		<Card>
			<CardHeader
				title="Cash on hand → banking"
				action={
					canConnect ? (
						<Link to="/settings/revolut" className={cardActionClassName}>
							Revolut →
						</Link>
					) : undefined
				}
			/>
			<CashAccounts />
		</Card>
	);
}

/**
 * The accounts, grouped by currency in the tile's order, each currency several accounts hold with its total, and the
 * figure when there is one currency or a converted total; converted, each balance says what it comes to. With none to
 * show, it says why and who can change that: an owner or admin gets the way to the Revolut settings, anyone else the
 * name of who can.
 */
function CashAccounts() {
	const { canRead, canConnect, data, isPending, isError, error, primary, rates, now, detailsPending } = useCash();
	if (!canRead) {
		return <Empty>Only members with access to Finance see the bank balances.</Empty>;
	}
	if (isPending || detailsPending) {
		return (
			<div role="status" aria-label="Loading cash accounts" className="flex flex-col gap-3 p-4">
				<Skeleton className="h-5 w-1/2" />
				<Skeleton className="h-3.5 w-2/3" />
				<Skeleton className="h-3.5 w-3/5" />
			</div>
		);
	}
	if (isError) {
		return (
			<p role="alert" className="px-4 py-3.5 text-caption text-alert">
				{error.message}
			</p>
		);
	}
	if (data.status !== "connected") {
		return <NoBank status={data.status} canConnect={canConnect} />;
	}
	const position = cashPosition(data.accounts, rates, primary, now);
	const accounts = plural(data.accounts.length, "account", "accounts");
	const { figure, held, order } = cardSummary(position);
	return (
		<>
			<div className="px-4 pt-3.5 pb-1">
				{figure === undefined ? null : (
					<div className="mb-[5px] text-figure font-semibold tracking-title text-ink tabular-nums">
						{figure}
					</div>
				)}
				<div className="text-tiny text-ink-3">{`${accounts}${held} · Revolut`}</div>
				{position.kind === "converted" && position.approximate ? <ConversionNote position={position} /> : null}
			</div>
			<Table
				label="Cash accounts"
				columns={cashColumns(position)}
				rows={revolutCashRows(data.accounts, order)}
				rowKey={row => (row.kind === "account" ? row.account.id : `total-${row.currency}`)}
				fit
			/>
		</>
	);
}

/**
 * What the card leads with: its figure — one currency's total, or the converted one — the currencies the accounts are
 * in, and the order of the table's currencies, which is the tile's.
 */
function cardSummary(position: CashPosition): {
	readonly figure: string | undefined;
	readonly held: string;
	readonly order: readonly string[];
} {
	switch (position.kind) {
		case "none": {
			return { figure: undefined, held: "", order: [] };
		}
		case "single": {
			const { amount, currency } = position.total;
			return { figure: formatMoney(amount, currency), held: ` in ${currency}`, order: [currency] };
		}
		case "unconverted": {
			const order = position.totals.map(each => each.currency);
			return { figure: undefined, held: ` in ${String(order.length)} currencies`, order };
		}
		case "converted": {
			const { approximate, conversion } = position;
			const order = [...conversion.converted, ...conversion.unconverted].map(each => each.currency);
			return {
				figure: `${approximate ? "≈ " : ""}${formatMoney(conversion.total, conversion.target)}`,
				held: ` in ${String(order.length)} currencies`,
				order,
			};
		}
	}
}

/**
 * How the card's figure was converted: into which currency, which the Settings › Identity page sets; at the ECB's
 * reference rates of which day, said in the warning colour when they are a week old or more; and each rate used.
 */
function ConversionNote({ position }: { readonly position: Extract<CashPosition, { kind: "converted" }> }) {
	const { conversion, date, ageInDays } = position;
	const { target } = conversion;
	const rates = conversion.converted.filter(each => each.currency !== target);
	return (
		<>
			<p className={cn("mt-1 text-tiny", isStale(ageInDays) ? "text-warn" : "text-ink-3")}>
				{`In ${target}, the `}
				<Link to="/settings/identity" className="text-accent hover:underline">
					primary currency
				</Link>
				{`, at the ECB's reference rates of ${formatShortDate(date)}`}
				{isStale(ageInDays) ? ` · ${String(ageInDays)} days old` : null}
			</p>
			{rates.length === 0 ? null : (
				<ul aria-label="Exchange rates" className="flex flex-wrap gap-x-3 text-tiny text-ink-3 tabular-nums">
					{rates.map(each => (
						<li key={each.currency} className="whitespace-nowrap">
							{`1 ${each.currency} = ${formatRate(each.rate)} ${target}`}
						</li>
					))}
				</ul>
			)}
		</>
	);
}

interface NoBankProps {
	readonly status: Exclude<RevolutAccounts["status"], "connected">;
	readonly canConnect: boolean;
}

function NoBank({ status, canConnect }: NoBankProps) {
	if (status === "unavailable") {
		return <Empty>No bank can be connected here yet.</Empty>;
	}
	const refused = status === "reconnect";
	return (
		<Empty>
			{refused ? "Revolut no longer accepts the connection." : "No bank connected yet."}{" "}
			{canConnect ? (
				<Link to="/settings/revolut" className="text-accent hover:underline">
					{refused ? "Reconnect Revolut in Settings" : "Connect Revolut in Settings"}
				</Link>
			) : refused ? (
				"An owner or admin can reconnect it in Settings."
			) : (
				"An owner or admin can connect Revolut in Settings."
			)}
		</Empty>
	);
}
